import React from 'react'
import {
  ColorFormat,
  Layer,
  Rect,
  Appearance as AppearanceType,
  SymbolData,
  ColorVariable,
} from 'modules/shares/types'
import { InspectorData } from 'modules/shares/Inspector/types'
import Overview from '../Overview'
import PositionAndSize from '../PositionAndSize'
import TextOrLayerStyles from '../TextOrLayerStyles'
import Style from '../Style'
import Text from '../Text'
import Appearance, { isAppearanceHidden } from '../Appearance'
import Content from '../Content'
import { SymbolInSidebar } from '../SymbolInSidebar'
import Exports from '../Exports'
import { VersionFragment } from 'gqlTypes'
import Box from 'components/Box'
import { formatPath } from 'modules/shares/Inspector/Sidebar/utils'

export interface LayerInfoProps {
  symbolInfo?: SymbolData
  layer: Layer
  onColorFormatChange: (f: ColorFormat) => void
  colorFormat: ColorFormat
  shareIdentifier: string
  symbolDocumentName: string
  currentVersion: VersionFragment
  colorVariables: { [key: string]: ColorVariable }
  hasArtboardExportableAssets?: boolean
  textStyles?: InspectorData['textStyles']
  layerStyles?: InspectorData['layerStyles']
  permanentArtboardShortId?: string
}

const LayerInfo: React.FC<LayerInfoProps> = ({
  symbolInfo,
  layer,
  onColorFormatChange,
  colorFormat,
  shareIdentifier,
  currentVersion,
  colorVariables,
  hasArtboardExportableAssets,
  symbolDocumentName,
  textStyles,
  layerStyles,
  permanentArtboardShortId,
}) => {
  const frame: Rect = layer.frame

  const IsPositionAndSizeVisible = !!frame
  const IsAppearanceVisible =
    layer.style?.appearance &&
    !isAppearanceHidden(
      layer.style.appearance.opacity,
      layer.style.appearance.blendMode,
      layer.cornerRadius
    )
  const isTextVisible = !!layer.attributedString
  const isStyleVisible = !!layer.style
  const isContentVisible = !!layer.attributedString?.string?.length
  const foreignSymbolShareId = symbolInfo?.foreignSymbolShareId
  const foreignVersionShortId = symbolInfo?.foreignVersionShortId

  // using isForeign to prevent the flickering while foreign share id is being loaded
  const documentName = symbolInfo?.symbolData?.isForeign
    ? symbolDocumentName
    : 'This Document'

  const symbolNamePath = symbolInfo?.symbolData?.name.split('/') || []
  const symbolName = symbolNamePath[symbolNamePath.length - 1]?.trim()

  symbolNamePath.splice(-1, 1)

  const path = formatPath([documentName, ...symbolNamePath])

  const isSymbolVisible =
    symbolInfo?.symbolData?.name && symbolInfo?.symbolLayer?.id && path !== ''

  return (
    <>
      <Overview layer={layer} />

      {IsPositionAndSizeVisible && (
        <PositionAndSize
          x={layer.frame.x}
          y={layer.frame.y}
          width={layer.originalFrame.width}
          height={layer.originalFrame.height}
          rotate={layer.rotation}
        />
      )}

      {textStyles && (
        <TextOrLayerStyles
          title="Text Styles"
          layer={layer}
          styles={textStyles}
        />
      )}

      {layerStyles && (
        <TextOrLayerStyles
          title="Layer Styles"
          layer={layer}
          styles={layerStyles}
        />
      )}

      {IsAppearanceVisible && (
        <Appearance
          radius={layer.cornerRadius}
          smoothCorners={layer.smoothCorners}
          {...(layer!.style!.appearance as AppearanceType)}
        />
      )}

      {isTextVisible && (
        <Text
          attributedString={layer.attributedString!}
          style={layer.style}
          onColorFormatChange={onColorFormatChange}
          colorFormat={colorFormat}
          colorVariables={colorVariables}
        />
      )}

      {isStyleVisible && (
        <Style
          style={layer.style!}
          onColorFormatChange={onColorFormatChange}
          colorFormat={colorFormat}
          colorVariables={colorVariables}
          layerType={layer.type}
        />
      )}

      {isContentVisible && <Content>{layer.attributedString!.string}</Content>}

      {isSymbolVisible && (
        <SymbolInSidebar
          name={symbolName}
          symbolArtboard={symbolInfo!.symbolArtboard}
          shareIdentifier={foreignSymbolShareId ?? shareIdentifier}
          foreignVersionShortId={foreignVersionShortId}
          path={path}
          isForeign={symbolInfo?.symbolData?.isForeign}
          permanentArtboardShortId={permanentArtboardShortId}
        />
      )}

      <Box marginTop="auto">
        <Exports
          for={{ type: 'layer', value: layer }}
          currentVersion={currentVersion}
          hasArtboardExportableAssets={hasArtboardExportableAssets}
        />
      </Box>
    </>
  )
}

export default LayerInfo
